/**
 * Workforce AI Assistant Page
 *
 * AI-powered chatbot for workforce intelligence:
 * - Answer questions about roles, certifications, training
 * - Provide candidate recommendations
 * - Analyze skill gaps
 * - Generate insights from workforce data
 */

import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Bot,
  Send,
  User,
  Loader2,
  Sparkles,
  Users,
  Target,
  TrendingUp,
  RefreshCw,
  ChevronRight,
  Lightbulb,
  ArrowLeft,
} from 'lucide-react';
import { getWeaveinApiUrl } from '@/src/config';
import { PageHeader } from '@/components/weavein';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { authFetch } from '@/lib/authFetch';

const getAuthToken = (): string | null => {
  return localStorage.getItem('auth_token');
};

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  sources?: string[];
}

interface Suggestion {
  text: string;
  icon: React.ReactNode;
}

interface MatchingCandidate {
  candidate_id: number;
  candidate_name: string;
  match_score: number;
  skills_match: number;
  certifications_match: number;
  experience_match: number;
  matching_skills: string[];
  missing_skills: string[];
}

interface SkillGap {
  skill_name: string;
  demand_count: number;
  supply_count: number;
  gap: number;
  gap_percentage: number;
}

export default function AIAssistantPage() {
  const { t } = useAppTranslation();
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState<'chat' | 'matching' | 'gaps'>('chat');
  const [roles, setRoles] = useState<Array<{ id: number; name: string }>>([]);
  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null);
  const [matchingResults, setMatchingResults] = useState<MatchingCandidate[]>([]);
  const [skillGaps, setSkillGaps] = useState<SkillGap[]>([]);
  const [loadingMatching, setLoadingMatching] = useState(false);
  const [loadingGaps, setLoadingGaps] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const defaultSuggestions: Suggestion[] = [
    { text: 'What certifications are required for CHW roles?', icon: <Sparkles className="w-4 h-4" /> },
    { text: 'Show me candidates ready for deployment', icon: <Users className="w-4 h-4" /> },
    { text: 'Which training curricula are most popular?', icon: <Target className="w-4 h-4" /> },
    { text: 'What are the current skill gaps in our workforce?', icon: <TrendingUp className="w-4 h-4" /> },
  ];

  useEffect(() => {
    fetchSuggestions();
    fetchRoles();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchSuggestions = async () => {
    const token = getAuthToken();
    if (!token) return;

    try {
      const res = await authFetch(getWeaveinApiUrl('/api/workforce/ai/chat/suggestions'));
      if (res.ok) {
        const data = await res.json();
        setSuggestions(data.suggestions || []);
      }
    } catch (error) {
      console.error('Error fetching suggestions:', error);
    }
  };

  const fetchRoles = async () => {
    const token = getAuthToken();
    if (!token) return;

    try {
      const res = await authFetch(getWeaveinApiUrl('/api/workforce/roles'));
      if (res.ok) {
        const data = await res.json();
        setRoles(data.items || []);
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  };

  const sendMessage = async (content: string) => {
    if (!content.trim() || isLoading) return;

    const token = getAuthToken();
    if (!token) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: content.trim(),
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      const res = await authFetch(getWeaveinApiUrl('/api/workforce/ai/chat'), {
        method: 'POST',
        jsonBody: {
          query: content.trim(),
          conversation_history: messages.slice(-10).map(m => ({
            role: m.role,
            content: m.content,
          })),
        },
      });

      if (res.ok) {
        const data = await res.json();
        const assistantMessage: Message = {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: data.message || 'I apologize, but I could not generate a response.',
          timestamp: new Date(),
          sources: data.sources?.map((s: any) => s.name) || [],
        };
        setMessages(prev => [...prev, assistantMessage]);
      } else {
        const assistantMessage: Message = {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: 'I apologize, but I encountered an error. Please try again.',
          timestamp: new Date(),
        };
        setMessages(prev => [...prev, assistantMessage]);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: 'I apologize, but I could not connect to the server. Please try again later.',
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, assistantMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(inputValue);
    }
  };

  const runCandidateMatching = async () => {
    if (!selectedRoleId) return;

    const token = getAuthToken();
    if (!token) return;

    setLoadingMatching(true);
    try {
      const res = await authFetch(getWeaveinApiUrl(`/api/workforce/ai/match-candidates?role_id=${selectedRoleId}&top_k=10`));
      if (res.ok) {
        const data = await res.json();
        setMatchingResults(data.matches || []);
      }
    } catch (error) {
      console.error('Error matching candidates:', error);
    } finally {
      setLoadingMatching(false);
    }
  };

  const analyzeSkillGaps = async () => {
    const token = getAuthToken();
    if (!token) return;

    setLoadingGaps(true);
    try {
      const res = await authFetch(getWeaveinApiUrl('/api/workforce/ai/skill-gaps'));
      if (res.ok) {
        const data = await res.json();
        setSkillGaps(data.gaps || []);
      }
    } catch (error) {
      console.error('Error analyzing skill gaps:', error);
    } finally {
      setLoadingGaps(false);
    }
  };

  const renderChat = () => (
    <div className="flex flex-col h-full">
      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mb-4">
              <Bot className="w-8 h-8 text-emerald-600" />
            </div>
            <h2 className="text-xl font-semibold text-ink mb-2">
              {t('workforce.ai.welcome', 'Workforce AI Assistant')}
            </h2>
            <p className="text-ink-500 mb-6 max-w-md">
              {t('workforce.ai.description', 'Ask me anything about your workforce, roles, certifications, or training programs.')}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-2xl">
              {defaultSuggestions.map((suggestion, index) => (
                <button
                  key={index}
                  onClick={() => sendMessage(suggestion.text)}
                  className="flex items-center space-x-3 p-4 bg-surface border border-v2border rounded-xl hover:border-emerald-300 hover:bg-emerald-50 transition-colors text-left"
                >
                  <span className="text-emerald-600">{suggestion.icon}</span>
                  <span className="text-sm text-ink-700">{suggestion.text}</span>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <>
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                    message.role === 'user'
                      ? 'bg-emerald-600 text-white'
                      : 'bg-surface border border-v2border text-ink-700'
                  }`}
                >
                  <div className="flex items-start space-x-2">
                    {message.role === 'assistant' && (
                      <Bot className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                    )}
                    <div>
                      <p className="whitespace-pre-wrap">{message.content}</p>
                      {message.sources && message.sources.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-v2border-muted">
                          <p className="text-xs text-ink-500">Sources:</p>
                          <ul className="text-xs text-ink-400">
                            {message.sources.map((source, i) => (
                              <li key={i}>{source}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                    {message.role === 'user' && (
                      <User className="w-5 h-5 text-emerald-200 mt-0.5 flex-shrink-0" />
                    )}
                  </div>
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-surface border border-v2border rounded-2xl px-4 py-3">
                  <div className="flex items-center space-x-2">
                    <Bot className="w-5 h-5 text-emerald-600" />
                    <Loader2 className="w-4 h-4 animate-spin text-ink-400" />
                    <span className="text-ink-500">Thinking...</span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Input Area */}
      <div className="border-t border-v2border p-4 bg-surface">
        <div className="flex items-end space-x-3">
          <div className="flex-1 relative">
            <textarea
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={t('workforce.ai.placeholder', 'Ask about workforce, roles, or certifications...')}
              className="w-full px-4 py-3 pr-12 border border-v2border rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 resize-none"
              rows={1}
              disabled={isLoading}
            />
          </div>
          <button
            onClick={() => sendMessage(inputValue)}
            disabled={!inputValue.trim() || isLoading}
            className="p-3 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Send className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );

  const renderMatching = () => (
    <div className="p-6 space-y-6">
      <div className="bg-surface rounded-xl border border-v2border p-6">
        <h3 className="font-semibold text-ink mb-4 flex items-center">
          <Users className="w-5 h-5 mr-2 text-emerald-600" />
          {t('workforce.ai.matching.title', 'AI Candidate Matching')}
        </h3>
        <p className="text-ink-500 mb-4">
          {t('workforce.ai.matching.description', 'Find the best candidates for a role based on skills, certifications, and experience.')}
        </p>

        <div className="flex items-end space-x-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-ink-700 mb-1">
              {t('workforce.ai.matching.selectRole', 'Select Role')}
            </label>
            <select
              value={selectedRoleId || ''}
              onChange={(e) => setSelectedRoleId(Number(e.target.value) || null)}
              className="w-full px-3 py-2 border border-v2border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            >
              <option value="">{t('workforce.ai.matching.chooseRole', 'Choose a role...')}</option>
              {roles.map((role) => (
                <option key={role.id} value={role.id}>{role.name}</option>
              ))}
            </select>
          </div>
          <button
            onClick={runCandidateMatching}
            disabled={!selectedRoleId || loadingMatching}
            className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {loadingMatching ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Target className="w-4 h-4 mr-2" />
            )}
            {t('workforce.ai.matching.findMatches', 'Find Matches')}
          </button>
        </div>
      </div>

      {matchingResults.length > 0 && (
        <div className="bg-surface rounded-xl border border-v2border divide-y divide-gray-100">
          <div className="px-6 py-4">
            <h4 className="font-medium text-ink">
              {t('workforce.ai.matching.results', 'Matching Results')} ({matchingResults.length})
            </h4>
          </div>
          {matchingResults.map((candidate, index) => (
            <div key={candidate.candidate_id} className="px-6 py-4">
              <div className="flex items-start justify-between">
                <div className="flex items-center space-x-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-medium ${
                    index === 0 ? 'bg-yellow-500' : index === 1 ? 'bg-gray-400' : index === 2 ? 'bg-amber-600' : 'bg-gray-300'
                  }`}>
                    {index + 1}
                  </div>
                  <div>
                    <p className="font-medium text-ink">{candidate.candidate_name}</p>
                    <div className="flex items-center space-x-4 text-sm text-ink-500 mt-1">
                      <span>Skills: {Math.round(candidate.skills_match * 100)}%</span>
                      <span>Certs: {Math.round(candidate.certifications_match * 100)}%</span>
                      <span>Exp: {Math.round(candidate.experience_match * 100)}%</span>
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className={`text-lg font-bold ${
                    candidate.match_score >= 0.8 ? 'text-emerald-600' :
                    candidate.match_score >= 0.6 ? 'text-yellow-600' : 'text-ink-500'
                  }`}>
                    {Math.round(candidate.match_score * 100)}%
                  </div>
                  <p className="text-xs text-ink-400">match</p>
                </div>
              </div>
              {candidate.matching_skills.length > 0 && (
                <div className="mt-3">
                  <p className="text-xs text-ink-500 mb-1">Matching skills:</p>
                  <div className="flex flex-wrap gap-1">
                    {candidate.matching_skills.slice(0, 5).map((skill, i) => (
                      <span key={i} className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded text-xs">
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {candidate.missing_skills.length > 0 && (
                <div className="mt-2">
                  <p className="text-xs text-ink-500 mb-1">Missing skills:</p>
                  <div className="flex flex-wrap gap-1">
                    {candidate.missing_skills.slice(0, 3).map((skill, i) => (
                      <span key={i} className="px-2 py-0.5 bg-red-50 text-red-600 rounded text-xs">
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderSkillGaps = () => (
    <div className="p-6 space-y-6">
      <div className="bg-surface rounded-xl border border-v2border p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-semibold text-ink flex items-center">
              <TrendingUp className="w-5 h-5 mr-2 text-emerald-600" />
              {t('workforce.ai.gaps.title', 'Skill Gap Analysis')}
            </h3>
            <p className="text-ink-500 mt-1">
              {t('workforce.ai.gaps.description', 'Identify skills that are in demand but lacking in your workforce.')}
            </p>
          </div>
          <button
            onClick={analyzeSkillGaps}
            disabled={loadingGaps}
            className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 flex items-center"
          >
            {loadingGaps ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            {t('workforce.ai.gaps.analyze', 'Analyze')}
          </button>
        </div>
      </div>

      {skillGaps.length > 0 && (
        <div className="bg-surface rounded-xl border border-v2border divide-y divide-gray-100">
          <div className="px-6 py-4">
            <h4 className="font-medium text-ink">
              {t('workforce.ai.gaps.results', 'Identified Gaps')} ({skillGaps.length})
            </h4>
          </div>
          {skillGaps.map((gap, index) => (
            <div key={index} className="px-6 py-4">
              <div className="flex items-center justify-between mb-2">
                <p className="font-medium text-ink">{gap.skill_name}</p>
                <span className={`px-2 py-1 rounded text-xs font-medium ${
                  gap.gap_percentage >= 50 ? 'bg-red-100 text-red-700' :
                  gap.gap_percentage >= 25 ? 'bg-yellow-100 text-yellow-700' :
                  'bg-blue-100 text-blue-700'
                }`}>
                  {Math.round(gap.gap_percentage)}% gap
                </span>
              </div>
              <div className="flex items-center space-x-6 text-sm text-ink-500">
                <span>Demand: {gap.demand_count} roles</span>
                <span>Supply: {gap.supply_count} staff</span>
                <span>Gap: {gap.gap} positions</span>
              </div>
              <div className="mt-2 bg-paper-2 rounded-full h-2 overflow-hidden">
                <div
                  className={`h-full ${
                    gap.gap_percentage >= 50 ? 'bg-red-500' :
                    gap.gap_percentage >= 25 ? 'bg-yellow-500' : 'bg-emerald-500'
                  }`}
                  style={{ width: `${Math.min(100, gap.gap_percentage)}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {skillGaps.length === 0 && !loadingGaps && (
        <div className="bg-surface rounded-xl border border-v2border p-12 text-center">
          <Lightbulb className="w-12 h-12 text-ink-300 mx-auto mb-4" />
          <p className="text-ink-500">
            {t('workforce.ai.gaps.empty', 'Click "Analyze" to identify skill gaps in your workforce.')}
          </p>
        </div>
      )}
    </div>
  );

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        eyebrow="Workforce · AI Assistant"
        leading={
          <Link to="/workforce/dashboard" className="text-ink-400 hover:text-ink">
            <ArrowLeft className="w-5 h-5" />
          </Link>
        }
        title={t('workforce.ai.title', 'AI Assistant')}
        description={t('workforce.ai.subtitle', 'Powered by workforce intelligence')}
        tabs={[
          { key: 'chat', label: t('workforce.ai.tabs.chat', 'Chat') },
          { key: 'matching', label: t('workforce.ai.tabs.matching', 'Candidate Matching') },
          { key: 'gaps', label: t('workforce.ai.tabs.gaps', 'Skill Gaps') },
        ]}
        activeTab={activeTab}
        onTabChange={(key) => setActiveTab(key as 'chat' | 'matching' | 'gaps')}
      />

      {/* Content */}
      <div className="flex-1 overflow-hidden bg-paper">
        {activeTab === 'chat' && renderChat()}
        {activeTab === 'matching' && renderMatching()}
        {activeTab === 'gaps' && renderSkillGaps()}
      </div>
    </div>
  );
}
